import Link from '@material-ui/core/Link';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import { Button, Container, useMediaQuery } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import React, { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import { useRecoilValue } from 'recoil';
import useRouteUrlFactory from '../../../hooks/useRouteUrlFactory';
import { projectName, showContactPage, customNavigationLinks } from '../../../config';
import { localeState, regionState } from '../../../api/appState';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';
import { useAdminConfig } from '../../../adminConfig';
import { catalogueTaxonomiesHierarchySelector } from '../../../api/products/taxonomyTypes';
import AppFooterAdvantages from './AppFooterAdvantages';

export interface AppFooterProps {
  handleDrawerToggle: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const useStyles = makeStyles(
  (theme) => ({
    root: {},
    content: {
      backgroundColor: theme.palette.appFooter.background,
      color: theme.palette.appFooter.color,
      display: 'flex',
      flexDirection: 'column',
      padding: theme.spacing(1, 0),
      '& ul': {
        listStyle: 'none',
        paddingLeft: 0,
      },
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(2),
      },
    },
    navLinkContainer: {
      paddingBottom: theme.spacing(2),
    },
    navLink: {
      position: 'relative',
      color: theme.palette.appFooter.color,
      textDecoration: 'none',
      paddingLeft: theme.spacing(2),
      '&:hover': {
        textDecoration: 'underline',
        '&:before': {
          left: theme.spacing(0.5),
        },
      },
      '&:before': {
        maxWidth: theme.spacing(2),
        content: '">"',
        transform: 'scaleY(2) scaleX(0.8)',
        position: 'absolute',
        left: 0,
        transitionDuration: '0.3s',
      },
    },
    information: {
      display: 'flex',
      gap: theme.spacing(2),
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
      justifyContent: 'space-between',
      width: '100%',
      padding: theme.spacing(2, 1),
    },
    baseInfo: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      justifyContent: 'start',
      alignItems: 'center',
      '& img': {
        maxHeight: '50px',
      },
      [theme.breakpoints.up('sm')]: {
        flexDirection: 'row',
        justifyContent: 'space-between',
      },
      [theme.breakpoints.up('md')]: {
        flexDirection: 'column',
        gap: theme.spacing(2),
      },
    },
    lists: {
      display: 'flex',
      width: '100%',
      alignItems: 'center',
      flexDirection: 'column',
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
        gap: theme.spacing(2),
        justifyContent: 'space-evenly',
        alignItems: 'normal',
      },
    },
    list: {
      width: '100%',
      borderBottom: `1px solid grey`,
      [theme.breakpoints.up('md')]: {
        padding: theme.spacing(1),
        borderBottom: 'none',
      },
    },
    listHeadline: {
      display: 'none',
      marginTop: 0,
      [theme.breakpoints.up('md')]: {
        display: 'block',
      },
    },
    listButton: {
      color: theme.palette.appFooter.color,
      width: '100%',
      '&:hover': {
        backgroundColor: 'transparent',
        textDecoration: 'underline',
      },
      [theme.breakpoints.up('md')]: {
        display: 'none',
      },
    },
    listButtonLabel: {
      margin: 0,
      '&:after': {
        maxWidth: theme.spacing(2),
        content: '"V"',
        transform: 'scaleY(0.6) scaleX(2)',
        position: 'absolute',
        right: 8,
        transitionDuration: '0.5s',
        fontWeight: 400,
      },
      '&.open::after': {
        transform: 'scaleY(0.6) scaleX(2) rotateX(180deg)', // otočení šipky
      },
    },
    listElements: {
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    copyright: {
      borderTop: `1px solid white`,
      fontSize: '12px',
      paddingTop: theme.spacing(1),
      textAlign: 'center',
    },
    infoDetail: {
      display: 'flex',
      flexDirection: 'row',
      margin: theme.spacing(1),
      maxWidth: 'max-content',
    },
    infoDetailIcon: {
      width: 24,
      height: 24,
      marginRight: theme.spacing(2),
    },
    infoDetailtText: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
    },
    infoDetailtLabel: {
      fontWeight: 'bold',
      fontSize: '1rem',
      textDecoration: 'none',
      color: theme.palette.appFooter.color,
      [theme.breakpoints.up('sm')]: {
        whiteSpace: 'nowrap',
      },
    },
    infoDetailDescription: {
      fontSize: '12px',
    },
  }),
  { name: 'AppFooter' },
);

type InfoDetailType = 'allAboutShopping' | 'store' | 'usefulInformation';

export default function AppFooter(): JSX.Element {
  const classes = useStyles();
  const t = useTranslateWithFallback();
  const routeUrlFactory = useRouteUrlFactory();
  const currentYear = new Date().getFullYear();
  // const { footerContactBox, footerHeadquarters, staticPageList } = useAdminConfig();
  const taxonomyTree = useRecoilValue(catalogueTaxonomiesHierarchySelector);
  const [showInfoDetail, setShowInfoDetail] = useState<Record<InfoDetailType, boolean>>({
    allAboutShopping: true,
    store: true,
    usefulInformation: true,
  });
  const region = useRecoilValue(regionState);

  const theme = useTheme();
  const isAboveMedium = useMediaQuery(theme.breakpoints.up('md'));

  const taxonomies = taxonomyTree.children || [];

  const locale = useRecoilValue(localeState);
  const { staticPageList } = useAdminConfig();

  const handleToggle = useCallback(
    (type: InfoDetailType) => () => {
      if (isAboveMedium === false) {
        setShowInfoDetail((prev) => ({
          ...prev,
          [type]: !prev[type],
        }));
      }
    },
    [isAboveMedium, setShowInfoDetail],
  );

  useEffect(() => {
    if (isAboveMedium) {
      setShowInfoDetail({
        allAboutShopping: true,
        store: true,
        usefulInformation: true,
      });
    } else {
      setShowInfoDetail({
        allAboutShopping: false,
        store: false,
        usefulInformation: false,
      });
    }
  }, [isAboveMedium]);

  return (
    <div className={classes.root}>
      <AppFooterAdvantages />
      <div className={classes.content}>
        <Container>
          <div className={classes.information}>
            <div className={classes.baseInfo}>
              <img className={classes.infoDetail} src={`/${projectName}/logo_white.svg`} alt={`${projectName} logo`} />

              <div className={classes.infoDetail}>
                <img
                  className={classes.infoDetailIcon}
                  src={`/${projectName}/icons/phone_icon.svg`}
                  alt={'Phone icon'}
                />
                <div className={classes.infoDetailtText}>
                  <a className={classes.infoDetailtLabel} href='tel:+420734878510'>
                    +420 734 878 510
                  </a>
                  <span className={classes.infoDetailDescription}>(po-pá 8:00 - 17:00)</span>
                </div>
              </div>
              <div className={classes.infoDetail}>
                <img
                  className={classes.infoDetailIcon}
                  src={`/${projectName}/icons/email_icon.svg`}
                  alt={'Email icon'}
                />
                <div className={classes.infoDetailtText}>
                  <a className={classes.infoDetailtLabel} href='mailto:info@mcare.cz'>
                    info@mcare.cz
                  </a>
                  <span className={classes.infoDetailDescription}>{t('mainNavigation/infoDetailDescription')}</span>
                </div>
              </div>
            </div>
            <div className={classes.lists}>
              <div className={classes.list}>
                <h3 className={classes.listHeadline}>{t('mainNavigation/allAboutShopping')}</h3>
                <Button onClick={handleToggle('allAboutShopping')} className={classes.listButton}>
                  <h3 className={clsx(classes.listButtonLabel, showInfoDetail.allAboutShopping ? 'open' : '')}>
                    {t('mainNavigation/allAboutShopping')}
                  </h3>
                </Button>
                <ul className={showInfoDetail.allAboutShopping ? '' : classes.listElements}>
                  {staticPageList
                    ?.filter(({ group }) => group === 'gdpr' || group === 'tos')
                    .map((i, index) => (
                      <li key={index} className={classes.navLinkContainer}>
                        <Link
                          component={RouterLink}
                          to={routeUrlFactory('staticPage', {
                            params: { group: i.group, locale },
                          })}
                          color='textPrimary'
                          variant='inherit'
                          className={classes.navLink}
                          noWrap
                        >
                          {i.title || i.group}
                        </Link>
                      </li>
                    ))}
                </ul>
              </div>
              <div className={classes.list}>
                <h3 className={classes.listHeadline}>{t('mainNavigation/store')}</h3>
                <Button onClick={handleToggle('store')} className={classes.listButton}>
                  <h3 className={clsx(classes.listButtonLabel, showInfoDetail.store ? 'open' : '')}>
                    {t('mainNavigation/store')}
                  </h3>
                </Button>

                <ul className={showInfoDetail.store ? '' : classes.listElements}>
                  {taxonomies.map((taxonomy) => (
                    <li key={taxonomy.code} className={classes.navLinkContainer}>
                      <Link
                        component={RouterLink}
                        // to={`/${locale}${taxonomy.code}`}
                        to={taxonomy.code}
                        color='textPrimary'
                        variant='inherit'
                        className={classes.navLink}
                        noWrap
                      >
                        {t(taxonomy.label)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className={classes.list}>
                <h3 className={classes.listHeadline}>{t('mainNavigation/usefulInformation')}</h3>
                <Button onClick={handleToggle('usefulInformation')} className={classes.listButton}>
                  <h3 className={clsx(classes.listButtonLabel, showInfoDetail.usefulInformation ? 'open' : '')}>
                    {' '}
                    {t('mainNavigation/usefulInformation')}
                  </h3>
                </Button>

                <ul className={showInfoDetail.usefulInformation ? '' : classes.listElements}>
                  <li className={classes.navLinkContainer}>
                    <Link
                      component={RouterLink}
                      to={routeUrlFactory('homePage')}
                      color='textPrimary'
                      variant='inherit'
                      className={classes.navLink}
                      noWrap
                    >
                      {t('mainNavigation/introduction')}
                    </Link>
                  </li>
                  {showContactPage && (
                    <li className={classes.navLinkContainer}>
                      <Link
                        component={RouterLink}
                        to={routeUrlFactory('contacts')}
                        color='textPrimary'
                        variant='inherit'
                        className={classes.navLink}
                        noWrap
                      >
                        {t('mainNavigation/contacts')}
                      </Link>
                    </li>
                  )}
                  <li className={classes.navLinkContainer}>
                    <a
                      href={
                        region === 'SK' ? 'https://www.maxcare.sk/partner' : 'https://www.maxcare.cz/partner'
                      }
                      target='_blank'
                      rel='noreferrer'
                      className={classes.navLink}
                    >
                      {t('mainNavigation/registrationForPartners')}
                    </a>
                  </li>
                  {customNavigationLinks &&
                    customNavigationLinks.map((link, idx) => (
                      <li key={idx} className={classes.navLinkContainer}>
                        <a href={link.href} target='_blank' rel='noreferrer' className={classes.navLink}>
                          {link.label[locale]}
                        </a>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>
          <div className={classes.copyright}>
            © {currentYear} {t('mainNavigation/allRightsReserved')}
          </div>
        </Container>
      </div>
    </div>
  );
}
